/**
 * Custom Incoming Hook
 * Demonstrates pipeline extensibility
 * Example: Enrich incoming messages with metadata, track source, validate sender
 */
import { BaseHook, type IPipelineContext, type HookResult } from 'edge-stream-js';
import { type HookMetadata } from '../HookMetadata';

export class CustomIncomingHook extends BaseHook {
  override name = 'CustomIncomingHook';
  override priority = 75; // Executes after ValidationHook (20) and FilterHook (50)

  metadata?: HookMetadata = {
    name: 'CustomIncomingHook',
    description: 'Enriches incoming messages with metadata, detects spam, validates message length, and tracks metrics (word count, character count, URLs, mentions)',
    version: '1.0.0',
    author: 'BizFirstGO',
    tags: ['enrichment', 'incoming', 'spam-detection', 'metrics'],
    modifiesData: true,
    impactLevel: 'high',
  };

  async execute(context: IPipelineContext): Promise<HookResult> {
    const envelope = context.envelope as any;
    const messageId = envelope.meta.id;
    const body = envelope.body as any;
    const messageData = body?.data?.data || {};

    console.log(`[CustomIncomingHook] 🎯 Processing custom incoming logic for: ${messageId}`);

    // Example 1: Enrich message with metadata
    messageData.receivedAt = new Date().toISOString();
    messageData.source = 'signalr-hub';
    messageData.version = '1.0';

    // Example 2: Validate message length
    if (messageData.text && messageData.text.length > 5000) {
      console.warn(`[CustomIncomingHook] ⚠️ Message exceeds length limit: ${messageId}`);
      throw new Error('Message text exceeds maximum length of 5000 characters');
    }

    // Example 3: Check for profanity/spam (simplified)
    const bannedWords = ['spam', 'test123']; // Demo list
    const textLower = (messageData.text || '').toLowerCase();
    const foundBannedWords = bannedWords.filter(word => textLower.includes(word));

    if (foundBannedWords.length > 0) {
      console.warn(`[CustomIncomingHook] 🚫 Spam detected: ${foundBannedWords.join(', ')}`);
      throw new Error(`Message contains prohibited content: ${foundBannedWords.join(', ')}`);
    }

    // Example 4: Track message metrics
    const metrics = {
      wordCount: (messageData.text || '').split(/\s+/).length,
      characterCount: (messageData.text || '').length,
      hasUrls: /(http|https):\/\//.test(messageData.text || ''),
      hasMentions: /@/.test(messageData.text || ''),
    };

    messageData.metrics = metrics;

    console.log(`[CustomIncomingHook] ✅ Custom processing complete: ${messageId}`, {
      metrics,
      enriched: true,
    });

    // Must return hook result for pipeline to continue
    return this.continue();
  }
}
